import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import Button from "../../atoms/Button";
import Select from "../../atoms/Select";
import LoadingSpinner from "../../atoms/LoadingSpinner";
import CommentRow from "../../molecules/CommentRow";
import useUserDirectoryResolver from "../../../hooks/useUserDirectoryResolver";
import {
    addGroupCardComment,
    deleteGroupCardComment,
    listGroupCardComments,
    updateGroupCardComment,
} from "../../../services/groupService";
import { resolveUserAvatarUrl, resolveUserDisplayName } from "../../../utils/userIdentity";

export const BOARD_PRIORITY_VALUES = ["CRITICAL", "HIGH", "MEDIUM", "LOW"];
const COMMENT_PAGE_SIZE = 20;
const BOARD_MODERATOR_ROLES = ["OWNER", "ADMIN", "MODERATOR"];
export const getBoardPriorityOptions = (t) => BOARD_PRIORITY_VALUES.map((value) => ({
    label: t(`bandsPage.board.priority.${value.toLowerCase()}`),
    value,
}));

const formatDateTimeLocal = (isoString) => {
    if (!isoString) {
        return "";
    }
    const date = new Date(isoString);
    if (Number.isNaN(date.getTime())) {
        return "";
    }
    const pad = (value) => value.toString().padStart(2, "0");
    const year = date.getFullYear();
    const month = pad(date.getMonth() + 1);
    const day = pad(date.getDate());
    const hours = pad(date.getHours());
    const minutes = pad(date.getMinutes());
    return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const parseDateTimeLocal = (value) => {
    if (!value) {
        return null;
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return null;
    }
    return date.toISOString();
};

export default function BandBoardCardModal({
    isOpen = false,
    card = null,
    listId = "",
    lists = [],
    members = [],
    groupId = "",
    boardId = "",
    currentUserId = null,
    onClose,
    onSave,
    onMove,
    onDelete,
    onToggleChecklistItem,
    isProcessing = false,
    isDeleting = false,
    error = null,
    pendingChecklistItemIds = {},
}) {
    const { t } = useTranslation();
    const [draft, setDraft] = useState({
        title: "",
        description: "",
        dueAt: "",
        priority: "MEDIUM",
        storyPoints: "",
        assigneeId: "",
    });
    const [selectedListId, setSelectedListId] = useState(listId != null ? String(listId) : "");
    const [comments, setComments] = useState([]);
    const [commentsTotal, setCommentsTotal] = useState(0);
    const [commentsPage, setCommentsPage] = useState(0);
    const [isCommentsLoading, setIsCommentsLoading] = useState(false);
    const [commentDraft, setCommentDraft] = useState("");
    const [isCommentSubmitting, setIsCommentSubmitting] = useState(false);
    const [commentError, setCommentError] = useState(null);
    const [editingCommentId, setEditingCommentId] = useState(null);
    const resolveMemberProfile = useUserDirectoryResolver({ members });

    const priorityOptions = useMemo(() => getBoardPriorityOptions(t), [t]);

    useEffect(() => {
        if (!isOpen) {
            return;
        }
        setDraft({
            title: card?.title || "",
            description: card?.description || "",
            dueAt: formatDateTimeLocal(card?.dueAt),
            priority: card?.priority || "MEDIUM",
            storyPoints: card?.storyPoints != null ? String(card.storyPoints) : "",
            assigneeId: card?.assigneeId != null ? String(card.assigneeId) : "",
        });
        setSelectedListId(listId != null ? String(listId) : "");
    }, [card, isOpen, listId]);

    const memberOptions = useMemo(() => {
        if (!Array.isArray(members)) {
            return [];
        }
        return members
            .filter((member) => member?.status === "ACTIVE" && member?.userId != null)
            .map((member) => {
                const profile = typeof resolveMemberProfile === "function" ? resolveMemberProfile(member.userId) : null;
                const user = member?.user || {};
                const label = resolveUserDisplayName(profile)
                    || resolveUserDisplayName(member)
                    || resolveUserDisplayName(user)
                    || t("bandsPage.members.userFallback", { id: member.userId });
                return {
                    label,
                    value: String(member.userId),
                };
            });
    }, [members, resolveMemberProfile, t]);

    const currentUserIdString = currentUserId != null ? String(currentUserId) : null;
    const currentMembership = useMemo(() => {
        if (!currentUserIdString || !Array.isArray(members)) {
            return null;
        }
        return members.find(
            (member) =>
                member
                && member.status === "ACTIVE"
                && member.userId != null
                && String(member.userId) === currentUserIdString
        ) || null;
    }, [members, currentUserIdString]);
    const canModerateBoard = useMemo(
        () => Boolean(currentMembership?.role && BOARD_MODERATOR_ROLES.includes(currentMembership.role)),
        [currentMembership]
    );

    const normalizeCommentBody = useCallback((value) => {
        if (value == null) {
            return "";
        }
        try {
            return String(value).trim();
        } catch (error) {
            return String(value || "").trim();
        }
    }, []);

    const resolveCommentAuthor = useCallback((comment) => {
        const userId = comment?.userId;
        const profile = typeof resolveMemberProfile === "function" ? resolveMemberProfile(userId) : null;
        const member = Array.isArray(members)
            ? members.find((item) => item?.userId != null && String(item.userId) === String(userId))
            : null;
        const user = comment?.user || member?.user || {};
        const label = resolveUserDisplayName(comment)
            || resolveUserDisplayName(profile)
            || resolveUserDisplayName(member)
            || resolveUserDisplayName(user)
            || t("bandsPage.members.userFallback", { id: userId });
        return {
            name: label,
            avatarUrl: resolveUserAvatarUrl(comment)
                || resolveUserAvatarUrl(profile)
                || resolveUserAvatarUrl(user)
                || resolveUserAvatarUrl(member),
        };
    }, [members, resolveMemberProfile, t]);

    const loadComments = useCallback(async (page = 0, append = false) => {
        if (!groupId || !boardId || !card?.id) {
            return;
        }
        setIsCommentsLoading(true);
        if (!append) {
            setCommentError(null);
        }
        try {
            const response = await listGroupCardComments(groupId, boardId, card.id, { page, size: COMMENT_PAGE_SIZE });
            const items = Array.isArray(response?.comments)
                ? response.comments
                : Array.isArray(response?.items)
                    ? response.items
                    : [];
            if (append) {
                setComments((prev) => [...prev, ...items]);
            } else {
                setComments(items);
            }
            const totalValue = Number.isFinite(response?.total) ? response.total : items.length;
            setCommentsTotal(totalValue);
            setCommentsPage(page);
        } catch (loadError) {
            console.error("Failed to load comments", loadError);
            setCommentError(t("bandsPage.board.cardModal.comments.loadError"));
        } finally {
            setIsCommentsLoading(false);
        }
    }, [boardId, card?.id, groupId, t]);

    useEffect(() => {
        if (isOpen && card?.id && groupId && boardId) {
            setCommentDraft("");
            setCommentError(null);
            setEditingCommentId(null);
            loadComments(0, false);
        } else if (!isOpen) {
            setComments([]);
            setCommentsTotal(0);
            setCommentsPage(0);
            setCommentDraft("");
            setCommentError(null);
            setEditingCommentId(null);
        }
    }, [boardId, card?.id, groupId, isOpen, loadComments]);

    const handleSubmitComment = useCallback(async (event) => {
        event?.preventDefault();
        if (!groupId || !boardId || !card?.id) {
            return;
        }
        const normalized = normalizeCommentBody(commentDraft);
        if (!normalized) {
            setCommentError(t("bandsPage.board.cardModal.comments.emptyError"));
            return;
        }
        setIsCommentSubmitting(true);
        setCommentError(null);
        try {
            const created = await addGroupCardComment(groupId, boardId, card.id, { content: normalized });
            setComments((prev) => [...prev, created]);
            setCommentsTotal((prev) => (Number.isFinite(prev) ? prev + 1 : 1));
            setCommentDraft("");
        } catch (submitError) {
            console.error("Failed to add comment", submitError);
            const message = submitError?.response?.data?.message
                || t("bandsPage.board.cardModal.comments.submitError");
            setCommentError(message);
        } finally {
            setIsCommentSubmitting(false);
        }
    }, [boardId, card?.id, commentDraft, groupId, normalizeCommentBody, t]);

    const handleDeleteComment = useCallback(async (commentId) => {
        if (!groupId || !boardId || !card?.id) {
            return;
        }
        try {
            await deleteGroupCardComment(groupId, boardId, card.id, commentId);
            setComments((prev) => prev.filter((item) => String(item.id) !== String(commentId)));
            setCommentsTotal((prev) => Math.max(0, (Number.isFinite(prev) ? prev : 0) - 1));
        } catch (deleteError) {
            console.error("Failed to delete comment", deleteError);
            const message = deleteError?.response?.data?.message
                || t("bandsPage.board.cardModal.comments.deleteError");
            setCommentError(message);
        }
    }, [boardId, card?.id, groupId, t]);

    const handleEditComment = useCallback(async (commentId, nextContent) => {
        if (!groupId || !boardId || !card?.id) {
            return;
        }
        const normalized = normalizeCommentBody(nextContent);
        if (!normalized) {
            setCommentError(t("bandsPage.board.cardModal.comments.emptyError"));
            setEditingCommentId(null);
            return;
        }
        setCommentError(null);
        try {
            const updated = await updateGroupCardComment(groupId, boardId, card.id, commentId, { content: normalized });
            setComments((prev) => prev.map((item) => (
                String(item.id) === String(commentId) ? { ...item, ...updated } : item
            )));
        } catch (updateError) {
            console.error("Failed to update comment", updateError);
            const message = updateError?.response?.data?.message
                || t("bandsPage.board.cardModal.comments.updateError");
            setCommentError(message);
        } finally {
            setEditingCommentId(null);
        }
    }, [boardId, card?.id, groupId, normalizeCommentBody, t]);

    const handleLoadMoreComments = useCallback(() => {
        if (isCommentsLoading) {
            return;
        }
        return loadComments(commentsPage + 1, true);
    }, [commentsPage, isCommentsLoading, loadComments]);

    const handleFieldChange = (field) => (event) => {
        const value = event.target.value;
        setDraft((current) => ({ ...current, [field]: value }));
    };

    const handleSave = async (event) => {
        event.preventDefault();
        if (!card) {
            return;
        }
        const payload = {};
        if (draft.title.trim() !== (card.title || "")) {
            payload.title = draft.title.trim();
        }
        if (draft.description !== (card.description || "")) {
            payload.description = draft.description;
        }
        const dueAtIso = parseDateTimeLocal(draft.dueAt);
        if (dueAtIso && dueAtIso !== card.dueAt) {
            payload.dueAt = dueAtIso;
        }
        if (draft.priority && draft.priority !== card.priority) {
            payload.priority = draft.priority;
        }
        if (draft.storyPoints.trim() !== "") {
            const storyPointsValue = Number(draft.storyPoints);
            if (!Number.isNaN(storyPointsValue) && storyPointsValue !== card.storyPoints) {
                payload.storyPoints = storyPointsValue;
            }
        } else if (card.storyPoints != null) {
            payload.storyPoints = null;
        }
        if (draft.assigneeId !== (card.assigneeId != null ? String(card.assigneeId) : "")) {
            payload.assigneeId = draft.assigneeId ? draft.assigneeId : null;
        }

        let success = true;
        if (Object.keys(payload).length > 0) {
            success = await onSave(payload);
        }

        if (!success) {
            return;
        }

        const currentListId = listId != null ? String(listId) : "";
        if (selectedListId && selectedListId !== currentListId) {
            const targetList = lists.find((list) => String(list.id) === String(selectedListId));
            const position = targetList?.cards?.length ?? 0;
            const moved = await onMove(selectedListId, position);
            if (!moved) {
                return;
            }
        }
    };

    const hasMoreComments = comments.length < commentsTotal;

    if (!isOpen) {
        return null;
    }

    return (
        <div className="band-board-card-modal" role="presentation" onClick={onClose}>
            <div
                className="band-board-card-modal__container"
                role="dialog"
                aria-modal="true"
                aria-labelledby="groupBoardCardTitle"
                onClick={(event) => event.stopPropagation()}
            >
                <header className="band-board-card-modal__header">
                    <div>
                        <h2 id="groupBoardCardTitle">
                            {card?.title || t("bandsPage.board.cardModal.headerTitle")}
                        </h2>
                        <p>
                            {card?.description
                                ? t("bandsPage.board.cardModal.headerDescription.withDetails")
                                : t("bandsPage.board.cardModal.headerDescription.empty")}
                        </p>
                    </div>
                    <button
                        type="button"
                        className="band-board-card-modal__close"
                        onClick={onClose}
                        aria-label={t("bandsPage.common.closeButton")}
                    >
                        ×
                    </button>
                </header>

                <div className="band-board-card-modal__content">
                    <form className="band-board-card-modal__form" onSubmit={handleSave}>
                        <label className="band-board-card-modal__field">
                            <span>{t("bandsPage.board.cardModal.fields.title")}</span>
                            <input
                                value={draft.title}
                                onChange={handleFieldChange("title")}
                                placeholder={t("bandsPage.board.cardModal.fields.titlePlaceholder")}
                                maxLength={240}
                            />
                        </label>

                        <label className="band-board-card-modal__field band-board-card-modal__field--description">
                            <span>{t("bandsPage.board.cardModal.fields.description")}</span>
                            <textarea
                                value={draft.description}
                                onChange={handleFieldChange("description")}
                                placeholder={t("bandsPage.board.cardModal.fields.descriptionPlaceholder")}
                                rows={8}
                            />
                        </label>

                        <div className="band-board-card-modal__field-row">
                            <label className="band-board-card-modal__field">
                                <span>{t("bandsPage.board.cardModal.fields.dueDate")}</span>
                                <input
                                    type="datetime-local"
                                    value={draft.dueAt}
                                    onChange={handleFieldChange("dueAt")}
                                />
                            </label>
                            <label className="band-board-card-modal__field">
                                <span>{t("bandsPage.board.cardModal.fields.priority")}</span>
                                <Select
                                    value={draft.priority}
                                    onChange={handleFieldChange("priority")}
                                    options={priorityOptions}
                                    className="band-board-card-modal__select"
                                />
                            </label>
                        </div>

                        <div className="band-board-card-modal__field-row">
                            <label className="band-board-card-modal__field">
                                <span>{t("bandsPage.board.cardModal.fields.storyPoints")}</span>
                                <input
                                    type="number"
                                    min="0"
                                    value={draft.storyPoints}
                                    onChange={handleFieldChange("storyPoints")}
                                    placeholder={t("bandsPage.board.cardModal.fields.storyPointsPlaceholder")}
                                />
                            </label>
                            <label className="band-board-card-modal__field">
                                <span>{t("bandsPage.board.cardModal.fields.assignee")}</span>
                                <Select
                                    value={draft.assigneeId}
                                    onChange={handleFieldChange("assigneeId")}
                                    options={[{ label: t("bandsPage.board.cardModal.fields.assigneePlaceholder"), value: "" }, ...memberOptions]}
                                    className="band-board-card-modal__select"
                                />
                            </label>
                        </div>

                        <label className="band-board-card-modal__field">
                            <span>{t("bandsPage.board.cardModal.fields.list")}</span>
                            <Select
                                value={selectedListId}
                                onChange={(event) => setSelectedListId(event.target.value)}
                                className="band-board-card-modal__select"
                                options={lists.map((list) => ({
                                    label: list.name,
                                    value: String(list.id),
                                }))}
                            />
                        </label>

                        {card?.checklists?.length ? (
                            <section className="band-board-card-modal__checklists">
                                {card.checklists.map((checklist) => {
                                    const totalItems = checklist.items?.length ?? 0;
                                    const completedItems = checklist.items?.filter((item) => item.completed).length ?? 0;
                                    const progress = totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0;
                                    return (
                                        <article key={checklist.id} className="band-board-card-modal__checklist">
                                            <header className="band-board-card-modal__checklist-header">
                                                <h3>{checklist.title}</h3>
                                                <span>{t("bandsPage.board.cardModal.checklists.progress", { percent: progress })}</span>
                                            </header>
                                            <ul className="band-board-card-modal__checklist-items">
                                                {checklist.items?.map((item) => {
                                                    const isPending = Boolean(pendingChecklistItemIds[item.id]);
                                                    return (
                                                        <li key={item.id}>
                                                            <label className="band-board-card-modal__checklist-item">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={item.completed}
                                                                    onChange={(event) =>
                                                                        onToggleChecklistItem(item.id, event.target.checked)
                                                                    }
                                                                    disabled={isPending || isProcessing}
                                                                />
                                                                <span className={item.completed ? "is-complete" : ""}>
                                                                    {item.label}
                                                                </span>
                                                            </label>
                                                        </li>
                                                    );
                                                })}
                                                {!checklist.items?.length && (
                                                    <li className="band-board-card-modal__checklist-empty">
                                                        {t("bandsPage.board.cardModal.checklists.emptyItems")}
                                                    </li>
                                                )}
                                            </ul>
                                        </article>
                                    );
                                })}
                            </section>
                        ) : (
                            <section className="band-board-card-modal__checklists band-board-card-modal__checklists--empty">
                                <p>{t("bandsPage.board.cardModal.checklists.none")}</p>
                            </section>
                        )}

                        {error && <p className="band-board-card-modal__error">{error}</p>}

                        <footer className="band-board-card-modal__actions">
                            <Button
                                type="button"
                                text={isDeleting ? t("bandsPage.board.cardModal.actions.deleting") : t("bandsPage.board.cardModal.actions.delete")}
                                variant="danger"
                                onClick={onDelete}
                                disabled={isProcessing || isDeleting}
                            />
                            <div className="band-board-card-modal__actions-right">
                                <Button
                                    type="button"
                                    text={t("bandsPage.board.cardModal.actions.cancel")}
                                    variant="secondary"
                                    onClick={onClose}
                                    disabled={isProcessing}
                                />
                                <Button
                                    type="submit"
                                    text={isProcessing ? t("bandsPage.board.cardModal.actions.saving") : t("bandsPage.board.cardModal.actions.save")}
                                    variant="primary"
                                    disabled={isProcessing}
                                />
                            </div>
                        </footer>
                    </form>

                    <aside
                        className="band-board-card-modal__comments-panel"
                        aria-label={t("bandsPage.board.cardModal.comments.title")}
                    >
                        <header className="band-board-card-modal__comments-header">
                            <div>
                                <p className="band-board-card-modal__comments-title">
                                    {t("bandsPage.board.cardModal.comments.title")}
                                </p>
                                <p className="band-board-card-modal__comments-subtitle">
                                    {t("bandsPage.board.cardModal.comments.subtitle")}
                                </p>
                            </div>
                            <span className="band-board-card-modal__comments-count">
                                {t("bandsPage.board.cardModal.comments.count", { count: commentsTotal })}
                            </span>
                        </header>
                        <div className="band-board-card-modal__comments-list" role="list" aria-busy={isCommentsLoading}>
                            {isCommentsLoading && !comments.length && (
                                <div className="band-board-card-modal__comments-loading">
                                    <LoadingSpinner />
                                    <span>{t("bandsPage.board.cardModal.comments.loading")}</span>
                                </div>
                            )}
                            {comments.map((comment) => {
                                const author = resolveCommentAuthor(comment);
                                const isCommentOwner = currentUserIdString && String(comment.userId) === currentUserIdString;
                                const canManageComment = Boolean(isCommentOwner || canModerateBoard);
                                return (
                                    <CommentRow
                                        key={comment.id}
                                        userId={comment.userId}
                                        username={author.name}
                                        avatarUrl={author.avatarUrl}
                                        createdAt={comment.createdAt}
                                        isEditing={String(editingCommentId) === String(comment.id)}
                                        onEdit={canManageComment ? () => setEditingCommentId(comment.id) : undefined}
                                        onCancelEdit={() => setEditingCommentId(null)}
                                        onSaveEdit={(nextHtml) => handleEditComment(comment.id, nextHtml)}
                                        onDelete={canManageComment ? () => handleDeleteComment(comment.id) : undefined}
                                        showReactions={false}
                                    >
                                        {comment.content}
                                    </CommentRow>
                                );
                            })}
                            {!isCommentsLoading && comments.length === 0 && (
                                <p className="band-board-card-modal__comments-empty">
                                    {t("bandsPage.board.cardModal.comments.empty")}
                                </p>
                            )}
                            {hasMoreComments && (
                                <button
                                    type="button"
                                    className="band-board-card-modal__comments-more"
                                    onClick={handleLoadMoreComments}
                                    disabled={isCommentsLoading}
                                >
                                    {isCommentsLoading
                                        ? t("bandsPage.board.cardModal.comments.loading")
                                        : t("bandsPage.board.cardModal.comments.loadMore")}
                                </button>
                            )}
                            {commentError && (
                                <p className="band-board-card-modal__error band-board-card-modal__comment-error">
                                    {commentError}
                                </p>
                            )}
                        </div>
                        <form className="band-board-card-modal__comment-form" onSubmit={handleSubmitComment}>
                            <label className="band-board-card-modal__field">
                                <span>{t("bandsPage.board.cardModal.comments.addLabel")}</span>
                                <textarea
                                    value={commentDraft}
                                    onChange={(event) => setCommentDraft(event.target.value)}
                                    placeholder={t("bandsPage.board.cardModal.comments.placeholder")}
                                    rows={4}
                                />
                            </label>
                            <div className="band-board-card-modal__comment-actions">
                                <Button
                                    type="submit"
                                    text={isCommentSubmitting
                                        ? t("bandsPage.board.cardModal.comments.submitting")
                                        : t("bandsPage.board.cardModal.comments.submit")}
                                    variant="primary"
                                    disabled={isCommentSubmitting || isProcessing || !normalizeCommentBody(commentDraft)}
                                />
                            </div>
                        </form>
                    </aside>
                </div>
            </div>
        </div>
    );
}

BandBoardCardModal.propTypes = {
    isOpen: PropTypes.bool,
    card: PropTypes.shape({
        id: PropTypes.string,
        workItemId: PropTypes.string,
        title: PropTypes.string,
        description: PropTypes.string,
        dueAt: PropTypes.string,
        priority: PropTypes.string,
        storyPoints: PropTypes.number,
        assigneeId: PropTypes.string,
        checklists: PropTypes.arrayOf(
            PropTypes.shape({
                id: PropTypes.string,
                title: PropTypes.string,
                items: PropTypes.arrayOf(
                    PropTypes.shape({
                        id: PropTypes.string,
                        label: PropTypes.string,
                        completed: PropTypes.bool,
                    })
                ),
            })
        ),
    }),
    listId: PropTypes.string,
    lists: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string,
            name: PropTypes.string,
            cards: PropTypes.array,
        })
    ),
    members: PropTypes.arrayOf(
        PropTypes.shape({
            membershipId: PropTypes.string,
            userId: PropTypes.string,
            status: PropTypes.string,
            role: PropTypes.string,
        })
    ),
    groupId: PropTypes.string,
    boardId: PropTypes.string,
    currentUserId: PropTypes.string,
    onClose: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
    onMove: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired,
    onToggleChecklistItem: PropTypes.func.isRequired,
    isProcessing: PropTypes.bool,
    isDeleting: PropTypes.bool,
    error: PropTypes.string,
    pendingChecklistItemIds: PropTypes.object,
};
